'use client'
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { Icons } from '@crystal-eyes/components/elements/Icon/Icon'
import SectionCard from '../components/SectionCard'
import { DatumTags, DatumType } from '@crystal-eyes/types'
import usePersonality from '@crystal-eyes/hooks/profiles/usePersonality'
import styles from './QuickMeetingTips.module.scss'
import useDatums from '@crystal-eyes/hooks/profiles/useDatums'
import useAuth from '@crystal-eyes/hooks/useAuth'
import { track } from '@dashboard/lib/analytics_v2'

interface Identity {
  id: string
  rootSnapshot?: { id: string }
}

const OPTIONS_TO_TAGS: { [key: string]: DatumTags[] } = {
  'Give feedback': [DatumTags.GiveFeedback, DatumTags.PhoneCall],
  'Resolve a conflict': [DatumTags.ResolveConflict, DatumTags.PhoneCall],
  'Brainstorm ideas': [DatumTags.Brainstorm, DatumTags.PhoneCall],
  'Collaborate on a project': [DatumTags.Collaborate, DatumTags.PhoneCall],
  'Make a sales pitch': [DatumTags.SalesPitch, DatumTags.PhoneCall],
  'Make a good impression': [DatumTags.MakeGoodImpression, DatumTags.PhoneCall],
  'Persuade them to take action': [DatumTags.TakeAction, DatumTags.PhoneCall],
  'Schedule a meeting': [DatumTags.ScheduleMeeting, DatumTags.PhoneCall],
  Negotiate: [DatumTags.Negotiate, DatumTags.PhoneCall],
  'Discuss pricing': [DatumTags.DiscussPricing, DatumTags.PhoneCall],
  'Gather information': [DatumTags.GatheringInformation, DatumTags.PhoneCall],
  'Set expectation': [DatumTags.SettingExpectation, DatumTags.PhoneCall],
  'Give a demo': [DatumTags.Demo, DatumTags.PhoneCall],
  'Deliver bad news': [DatumTags.BadNews, DatumTags.PhoneCall],
  'Give instructions': [DatumTags.GiveInstructions, DatumTags.PhoneCall],
  'Ask for a raise': [DatumTags.AskRaise, DatumTags.PhoneCall],
  'Convince them to hire you': [DatumTags.ConvinceHire, DatumTags.PhoneCall],
  'Say thank you': [DatumTags.ThankYou, DatumTags.PhoneCall],
  'Follow up': [DatumTags.FollowUp, DatumTags.PhoneCall],
  'Say no': [DatumTags.SayNo, DatumTags.PhoneCall],
  'Connect on Linkedin': [DatumTags.LinkedIn, DatumTags.PhoneCall],
  Apologize: [DatumTags.Apologize, DatumTags.PhoneCall],
  Recruit: [DatumTags.Recruit, DatumTags.PhoneCall],
  'Introduce someone': [DatumTags.Introduce, DatumTags.PhoneCall],
  'Interview them': [DatumTags.Interview, DatumTags.PhoneCall],
}

export default function QuickMeetingTips({
  identity,
}: {
  identity?: Identity
}) {
  const {
    data: personality,
    loading: personalityLoading,
    error: personalityError,
  } = usePersonality({ identity })
  const { data: auth } = useAuth()
  const [selectedOption, setSelectedOption] =
    useState<keyof typeof OPTIONS_TO_TAGS>('Brainstorm ideas')

  useEffect(() => {
    // analytics
    track('Quick Tips Advice Viewed', {
      location: 'Dashboard',
      want_to: 'Call',
      so_can: selectedOption,
      profile_id: '',
    })
  }, [selectedOption, auth?.jwtPayload])

  const { data: dos } = useDatums({
    personality,
    tags: OPTIONS_TO_TAGS[selectedOption],
    type: DatumType.Do,
    limit: 3,
  })

  const { data: donts } = useDatums({
    personality,
    tags: OPTIONS_TO_TAGS[selectedOption],
    type: DatumType.Dont,
    limit: 3,
  })

  const printLinkOption = OPTIONS_TO_TAGS[selectedOption]
    .filter(opt => opt !== DatumTags.PhoneCall)[0]
    .toLowerCase()

  const identIdComps = identity?.rootSnapshot?.id?.split(':')
  const profileId = identIdComps
    ? identIdComps[identIdComps.length - 1]
    : undefined

  if (!auth?.authed) return null

  const isPaywalled =
    dos?.find(datum => datum.premiumBlur) ||
    donts?.find(datum => datum.premiumBlur)

  if (!auth?.authed) return null
  if (!personalityLoading && !personalityError && !personality?.discType)
    return null

  return (
    <SectionCard
      cardId="quick_tips_meetings"
      icon={Icons.People}
      title="Quick Meeting Tips"
      sidebarTitle="Quick tips"
      className={styles.quickMeetingTips}
    >
      <div className="options">
        <select
          onChange={evt => setSelectedOption(evt.target.value)}
          className="CE select-menu"
          aria-label="Quick Tips Category"
        >
          {Object.keys(OPTIONS_TO_TAGS).map(opt => (
            <option value={opt} key={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="suggestions">
        <div
          className={`suggestions-section dos ${
            isPaywalled ? 'paywalled' : ''
          }`}
        >
          <h4>Dos</h4>
          {dos?.map(doDatum => (
            <div className="suggestion do" key={doDatum.id}>
              {doDatum.text}
            </div>
          ))}
        </div>

        <div
          className={`suggestions-section donts ${
            isPaywalled ? 'paywalled' : ''
          }`}
        >
          <h4>Dont's</h4>
          {donts?.map(dontDatum => (
            <div className="suggestion dont" key={dontDatum.id}>
              {dontDatum.text}
            </div>
          ))}
        </div>
      </div>

      {profileId && (
        <div className="cta">
          <Link
            href={`/app/print?uuid=${profileId}&action=phone_call&reason=${printLinkOption}`}
          >
            Print or Download Advice
          </Link>
        </div>
      )}
    </SectionCard>
  )
}
